import React from 'react';
import App from 'next/app';
import NextAppRouter from 'next/router';
import { BrowserMetrics, WebVital } from '@spotify-internal/semantic-metrics';
import { KeyboardDetectionProvider } from '@spotify-internal/encore-web';
import { RemoteConfigProvider } from '@spotify-internal/remote-config-integration-react';
import { Configuration } from '@spotify-internal/remote-config-properties';
import { captureException } from '@sentry/nextjs';

import { Header } from 'components/Header/Header';
import { Footer } from 'components/Footer/Footer';
import { sendMetric, withRouteMetrics } from 'utils/metrics';
import { getMasthead, MastheadProps } from 'utils/masthead/masthead';
import analytics from 'utils/client/analytics/analytics';
import MarketProvider from 'providers/market';
import MessagingProvider from 'providers/messaging/MessagingProvider';
import { TranslationProvider } from 'providers/translations/TranslationProvider';
import { setupLpTag } from 'lib/client/liveEngage';
import '@spotify-internal/masthead-react/index.css';
import '@spotify-internal/encore-web/css/encore-dark-theme.css';
import '@spotify-internal/encore-web/css/encore-light-theme.css';
import '@spotify-internal/encore-web/css/fonts.css';
import './global.css';
import { SharedPageData } from 'utils/shared-page-data';
import UbiLoggerProvider from 'utils/ubi/ubiProvider';

if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
  const ReactDOM = require('react-dom');
  const axe = require('@axe-core/react');
  axe(React, ReactDOM, 1000);
}

if (typeof window !== 'undefined') {
  setupLpTag();
}

interface CustomAppState extends MastheadProps {}

export class CustomApp extends App {
  componentDidCatch(error: Error) {
    captureException(error);
  }

  state: CustomAppState = { masthead: {} };

  async componentDidMount() {
    const { pageProps } = this.props;
    // No sharedPageData? We're on the error component
    if (!pageProps.sharedPageData) return;

    const { market, usePreview, isPlain } = pageProps.sharedPageData as SharedPageData;

    // Preview page should not show cookie bar. As a consequence, no tracking for these visits.
    if (!usePreview) {
      analytics.initialize(market.marketCode);
    }

    // No need to fetch masthead data for these pages
    if (!isPlain) {
      this.setState({ masthead: await getMasthead(market.marketCode) });
    }
  }

  render() {
    const { Component, pageProps } = this.props;

    // No sharedPageData? We're on the error component
    if (!pageProps.sharedPageData) {
      return <Component {...pageProps} />;
    }

    const { remoteConfigJSON, market, translations, isPlain, isLoggedIn } = pageProps.sharedPageData as SharedPageData;
    const { masthead } = this.state;

    const configuration = remoteConfigJSON ? Configuration.fromJSON(remoteConfigJSON) : null;
    return (
      <UbiLoggerProvider isLoggedIn={isLoggedIn}>
        <RemoteConfigProvider configuration={configuration}>
          <KeyboardDetectionProvider>
            <MarketProvider market={market}>
              <TranslationProvider translations={translations}>
                <MessagingProvider locale={market.locale} marketCode={market.marketCode}>
                  <Header data={masthead.header} />
                  <div role="main" className="mainContainer">
                    <Component {...pageProps} isPlain={isPlain} />
                  </div>
                  <Footer data={masthead.footer} />
                </MessagingProvider>
              </TranslationProvider>
            </MarketProvider>
          </KeyboardDetectionProvider>
        </RemoteConfigProvider>
      </UbiLoggerProvider>
    );
  }
}

export function reportWebVitals(metric: WebVital) {
  sendMetric(
    BrowserMetrics.getWebVitalsMetric(metric, {
      route: NextAppRouter.router?.route ?? 'unknown',
    }),
  );
}

export default withRouteMetrics(CustomApp as any);
